import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";
import { MapContainer, TileLayer, GeoJSON, useMap } from 'react-leaflet';
import Leaflet from "leaflet";
import { Icon, Label } from "semantic-ui-react";

import dataHelper from "../../helpers/dataHelper";

import "./Map.css";

const CENTER_BIH = [44.037789, 17.3510192];
const ZOOM_BIH = 8;

const centers = {
    "ugljevik": [44.6749794, 18.9599837],
    "gacko": [43.1884638, 18.4918456],
};

const zooms = {
    "ugljevik": 11.5,
    "gacko": 10.8,
};

function getCenterPosition(city) {
    return centers[city] ?? CENTER_BIH;
}

function getZoom(city) {
    return zooms[city] ?? ZOOM_BIH;
}

const GridSelectedButton = ({ onButtonClick }) => (
    <button className="grid-selected-button" onClick={onButtonClick}>
        <Label as='a'>
            Poništi izbor grida
            <Icon name='delete' />
        </Label>
    </button>
);

const Legend = ({
    max,
    pollutant,
    sector
}) => {
    const map = useMap();
    const legendRef = useRef();

    const getInnerHtml = (max) => `
    <div id="legend" class="legend p-xs" style="display: flex; flex-direction: column; align-items: center;">
    <table class="table-condensed" style="display: flex; flex-direction: column; justify-content: center; align-items: center; margin-top: 10px;">
      <tbody>
        <tr style="display: flex; width: 100%;">
          <td style="margin-right: auto;">0</td>
          <td style="text-align: right;">${max}</td>
        </tr>
        <tr style="display: flex; width: 100%;">
          <td class="App-map-legend-td" style="background-color: ${dataHelper.getColorForRange(1)};"></td>
          <td class="App-map-legend-td" style="background-color: ${dataHelper.getColorForRange(2)};"></td>
          <td class="App-map-legend-td" style="background-color: ${dataHelper.getColorForRange(3)};"></td>
          <td class="App-map-legend-td" style="background-color: ${dataHelper.getColorForRange(4)};"></td>
          <td class="App-map-legend-td" style="background-color: ${dataHelper.getColorForRange(5)};"></td>
          <td class="App-map-legend-td" style="background-color: ${dataHelper.getColorForRange(6)};"></td>
          <td class="App-map-legend-td" style="background-color: ${dataHelper.getColorForRange(7)};"></td>
          <td class="App-map-legend-td" style="background-color: ${dataHelper.getColorForRange(8)};"></td>
          <td class="App-map-legend-td" style="background-color: ${dataHelper.getColorForRange(9)};"></td>
          <td class="App-map-legend-td" style="background-color: ${dataHelper.getColorForRange(10)};"></td>
        </tr>
      </tbody>
    </table>
  </div>    
    `;

    useEffect(() => {
        const legendContainer = Leaflet.DomUtil.create('div', 'legend');

        const { value, unit } = dataHelper.getOptimalPollutantValue(pollutant, max, sector);
        const maxLegendValue = `${value} ${unit}`;
        legendContainer.innerHTML = getInnerHtml(maxLegendValue);

        const legendControl = Leaflet.control({ position: 'bottomleft' });
        legendControl.onAdd = function () {
            return legendContainer;
        };

        legendRef.current = legendControl;
        map.addControl(legendRef.current);

        return () => {
            map.removeControl(legendRef.current);
        };
    }, [map, max, pollutant, sector]);

    return null;
}

const Map = ({
    city,
    sector,
    pollutant,
    geoJSONData,
    maxPollutant,
    breaks,
    selectedGridId,
    setSelectedGridId
}) => {
    const geoJsonRef = useRef();
    const mapRef = useRef();

    useEffect(() => {
        if (!geoJsonRef.current) {
            return;
        }

        geoJsonRef.current.clearLayers();

        if (!geoJSONData) {
            return;
        }

        geoJsonRef.current.addData(geoJSONData);
    }, [geoJSONData, pollutant]);

    useEffect(() => {
        if (!mapRef.current) {
            return;
        }

        mapRef.current.setView(getCenterPosition(city), getZoom(city));
    }, [city])

    const whenClicked = ({ target }) => {
        const id = target?.feature?.properties?.id;
        setSelectedGridId(id);
    };

    const onGridCancel = () => {
        setSelectedGridId(undefined);
    };

    const pointToLayer = (feature, latlng) => {
        const customMarkerOptions = {
          radius: 6,
          fillColor: 'black',
          className: "App-map-point"
        };
      
        return Leaflet.circleMarker(latlng, customMarkerOptions);
    };

    const onEachFeature = (feature, layer) => {
        if (feature?.properties?.te) {            
            layer.bindTooltip(feature?.properties?.te, {
                direction: 'bottom',
                offset: [2, 0],
                permanent: true,
                className: "App-map-point-tooltip"
            });
        }
        layer.on({ click: whenClicked });
    };

    const getGridStyle = (feature) => {
        const id = feature?.properties?.id;

        if (id === selectedGridId) {
            return {
                color: "blue",
                weight: 1,
                opacity: 1,
                fillOpacity: 0.3,
            };
        };

        if (!pollutant) {
            return {
                color: "green",
                weight: 0.2,
                opacity: 1,
                fillOpacity: 0.3
            };
        }

        if (pollutant) {
            const value = dataHelper.getPollutantValueFromFeature(feature, pollutant);
            const position = dataHelper.getPositionInRanges(value, breaks);

            return {
                color: dataHelper.getColorForRange(position),
                weight: 0.2,
                opacity: 1,
                fillOpacity: 0.3
            };
        }
    };

    return (
        <>
            <MapContainer
                center={getCenterPosition(city)}
                zoom={getZoom(city)}
                zoomDelta={0.1}
                zoomSnap={false}
                className="App-map"
                scrollWheelZoom={true}
                ref={mapRef}
            >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                {geoJSONData && (
                    <GeoJSON
                        ref={geoJsonRef}
                        data={geoJSONData}
                        style={getGridStyle}
                        onEachFeature={onEachFeature}
                        pointToLayer={pointToLayer}
                    />
                )}
                {selectedGridId && (
                    <GridSelectedButton
                        onButtonClick={onGridCancel}
                    />
                )}
                {pollutant && <Legend max={maxPollutant} pollutant={pollutant} sector={sector} />}
            </MapContainer>
        </>
    );
};

Map.propTypes = {
    city: PropTypes.string,
    sector: PropTypes.string,
    pollutant: PropTypes.string,
    geoJSONData: PropTypes.object,
    maxPollutant: PropTypes.number,
    breaks: PropTypes.array,
    selectedGridId: PropTypes.number,
    setSelectedGridId: PropTypes.func,
};

Map.defaultProps = {
    city: "",
    sector: "",
    pollutant: "",
    geoJSONData: {},
    maxPollutant: 0,
    breaks: [],
    selectedGridId: null,
    setSelectedGridId: () => { },
};

export default Map;